import { normalizeText, sanitizeNamespace } from "@packages/commons";
import { type Attribute, PROCESSED_CHAR } from "./Compiler";
import { NlpCache, getNegativeAndPositiveDescriptions } from "./Nlp";
import type { Alias, Expression } from "./Parser";

const checkForParentLoop = (parent: CompilerScope | undefined, self: CompilerScope) => {
  if (!parent) {
    return;
  }
  let current: CompilerScope | undefined = parent;
  while (current) {
    if (self === current) {
      throw new Error("Parent loop detected");
    }
    current = current.parent;
  }
};

export const ENABLE_NEGATIVE_FORMS = false;

/**
 * A scope of compilation is a collection of attributes that are available in the current context.
 * During compilation, we may start to collect attributes in a nested scope, and then merge them back to the parent scope,
 * or we may discard the nested scope if it is not needed, for example if it turns out not to be valid.
 *
 * Scopes also store state between parses, such as aliases that are in scope.
 */
export default class CompilerScope {
  parent: CompilerScope | undefined;
  private readonly nlpCache: NlpCache;

  constructor(
    readonly name: string,
    parent?: CompilerScope,
  ) {
    checkForParentLoop(parent, this);
    this.parent = parent;
    this.nlpCache = parent?.nlpCache ?? new NlpCache();
  }

  private attributesByNamespacedDescription: Record<string, Attribute> = {};
  private attributesByDescription: Record<string, Record<string, Attribute>> = {};
  private attributesById: Record<string, Attribute> = {};
  private aliases: Record<string, Alias> = {};

  isRoot(): boolean {
    return !this.parent;
  }

  mergeToParent() {
    if (this.parent) {
      Object.assign(this.parent.attributesByNamespacedDescription, this.attributesByNamespacedDescription);
      for (const [description, attributes] of Object.entries(this.attributesByDescription)) {
        const parentAttributes = this.parent.attributesByDescription[description] || {};
        this.parent.attributesByDescription[description] = { ...parentAttributes, ...attributes };
      }
      Object.assign(this.parent.attributesById, this.attributesById);
      if (!this.parent.isRoot()) {
        Object.assign(this.parent.aliases, this.aliases);
      }
    }
  }

  addAlias(alias: Alias) {
    alias.text = alias.text.toLowerCase();
    this.aliases[alias.text] = alias;
  }

  getAliases(): Record<string, Alias> {
    return { ...this.parent?.getAliases(), ...this.aliases };
  }

  private getAttributeNamespacedKey(attribute: Attribute): string {
    if (!attribute.description) {
      throw new Error("Attribute must have a description");
    }
    return `${attribute.namespace || ""}///${normalizeText(attribute.description)}`.toLowerCase();
  }

  private addAttributeInternal(attribute: Attribute) {
    if (attribute.description) {
      const description = normalizeText(attribute.description);
      attribute.description = description;
      this.attributesByNamespacedDescription[this.getAttributeNamespacedKey(attribute)] = attribute;
      const attributesByDescription =
        this.attributesByDescription[description.toLowerCase()] ||
        (this.attributesByDescription[description.toLowerCase()] = {});
      attributesByDescription[attribute.namespace || ""] = attribute;

      if (attribute.namespace?.includes("/")) {
        const parts = attribute.namespace.split("/");
        while (parts.length) {
          const path = parts.join("/");
          attributesByDescription[path] = attribute;
          parts.pop();
        }
      }
    }
    this.attributesById[attribute.id] = attribute;
  }

  addAttribute(attribute: Attribute): Attribute {
    if (attribute.description?.includes("/")) {
      const parts = attribute.description.split("/");
      const actualDescription = parts.pop();
      const namespace = sanitizeNamespace(parts.join("/"));
      attribute.namespace = namespace;
      attribute.description = actualDescription || "";
    }

    if (attribute.description?.includes(PROCESSED_CHAR)) {
      throw new Error(`Attribute description cannot contain the processed char: ${PROCESSED_CHAR}`);
    }

    // we already know the form of the attribute OR it is not a new attribute, just add it
    if (attribute.form) {
      this.addAttributeInternal(attribute);
      return attribute;
    }

    const description = attribute.description && normalizeText(attribute.description);
    if (ENABLE_NEGATIVE_FORMS && description) {
      const { negativeDescription, positiveDescription } = getNegativeAndPositiveDescriptions(
        this.nlpCache,
        description,
      );

      if (positiveDescription === negativeDescription || !negativeDescription) {
        attribute.form = "positive";
        this.addAttributeInternal(attribute);
        return attribute;
      }
      if (description === negativeDescription) {
        let positiveAttribute = this.getAttributeByDescription(positiveDescription, attribute.namespace);
        if (!positiveAttribute) {
          positiveAttribute = { ...attribute, description: positiveDescription };
        }
        positiveAttribute.form = "positive";

        this.addAttributeInternal(positiveAttribute);
        attribute.derivativeFrom = positiveDescription;
        attribute.form = "negative";
        attribute.id = positiveAttribute.id;
        this.addAttributeInternal(attribute);
        return positiveAttribute;
      }
      if (description === positiveDescription) {
        let negativeAttribute = this.getAttributeByDescription(negativeDescription, attribute.namespace);
        if (!negativeAttribute) {
          negativeAttribute = { ...attribute, description: negativeDescription };
        }
        negativeAttribute.form = "negative";
        negativeAttribute.derivativeFrom = positiveDescription;
        negativeAttribute.id = attribute.id;
        this.addAttributeInternal(negativeAttribute);

        attribute.form = "positive";
        this.addAttributeInternal(attribute);
        return attribute;
      }

      console.error(JSON.stringify({ description, negativeDescription, positiveDescription }, null, 2));
      throw new Error("Positive/Negative description mismatch");
    }
    this.addAttributeInternal(attribute);
    return attribute;
  }

  getAttributeByDescription(description: string, currentNamespace?: string): Attribute | undefined {
    if (description.includes("/")) {
      const parts = description.split("/");
      const actualDescription = parts.pop();

      let namespace = parts.join("/");
      if (!namespace.startsWith("/")) {
        namespace = `/${namespace}`;
      }
      if (actualDescription) {
        const namespaceMap = this.attributesByDescription[normalizeText(actualDescription).toLowerCase()];
        const namespacedAttribute = namespaceMap?.[namespace.toLowerCase()];
        if (namespacedAttribute) {
          return namespacedAttribute;
        }
      }
    }

    const namespaceMap = this.attributesByDescription[normalizeText(description).toLowerCase()];
    if (namespaceMap) {
      if (currentNamespace) {
        const parts = currentNamespace.split("/");
        while (parts.length) {
          const path = parts.join("/");
          const attribute = namespaceMap[path.toLowerCase()];
          if (attribute) {
            return attribute;
          }

          parts.pop();
        }
      }

      const attributes = Object.values(namespaceMap);
      if (attributes.length === 1) {
        return attributes[0];
      }
    }

    return this.parent?.getAttributeByDescription(description, currentNamespace);
  }

  getAttributeById(id: string): Attribute | undefined {
    return this.attributesById[id] ?? this.parent?.getAttributeById(id);
  }

  getCapturedAttributes(): Attribute[] {
    return Object.values(this.attributesById).reduce((attributes, attribute) => {
      if (attribute.derivativeFrom) {
        const derivedFrom = this.getAttributeByDescription(attribute.derivativeFrom, attribute.namespace);
        if (derivedFrom) {
          attributes.push(derivedFrom);
        }
      } else {
        attributes.push(attribute);
      }
      return attributes;
    }, [] as Attribute[]);
  }

  getAllCapturedAttributes(): Attribute[] {
    if (this.parent) {
      return [...this.parent.getAllCapturedAttributes(), ...this.getCapturedAttributes()];
    }
    return this.getCapturedAttributes();
  }

  removeAttributeById(id: string) {
    const attribute = this.attributesById[id];
    if (!attribute) {
      return;
    }
    delete this.attributesById[id];
    if (attribute.description) {
      delete this.attributesByNamespacedDescription[this.getAttributeNamespacedKey(attribute)];
      const description = normalizeText(attribute.description).toLowerCase();
      const attributesByDescription = this.attributesByDescription[description];
      if (attributesByDescription && attribute.namespace) {
        const parts = attribute.namespace.split("/");
        while (parts.length) {
          const path = parts.join("/");
          if (attributesByDescription[path]?.id === id) {
            delete attributesByDescription[path];
          }
          parts.pop();
        }
      }
    }
  }

  getDepth(): number {
    return this.parent ? this.parent.getDepth() + 1 : 0;
  }
}

export class ParserCompilerScope extends CompilerScope {
  private expressionsToRemove: Record<string, Expression> = {};

  markExpressionToRemove(expression: Expression) {
    this.expressionsToRemove[expression.id] = expression;
  }

  getMarkedExpressionsToRemove(): Record<string, Expression> {
    return this.expressionsToRemove;
  }

  mergeToParent() {
    super.mergeToParent();
    if (this.parent instanceof ParserCompilerScope) {
      Object.assign(this.parent.expressionsToRemove, this.expressionsToRemove);
    }
  }

  getRoot() {
    let current: CompilerScope | undefined = this;
    while (current.parent) {
      current = current.parent;
    }
    return current;
  }
}
